import React, { useState, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { useArtisanSyncPage, getErrorMessage, getBlockingWorkOrder } from './hooks/useArtisanSyncPage';
import { ArtisanProfileTable } from './components/ArtisanProfileTable';
import { ArtisanUploadPanel } from './components/ArtisanUploadPanel';
import { ArtisanProfileDetailModal } from './components/ArtisanProfileDetailModal';
import { ArtisanDeleteDialog } from './components/ArtisanDeleteDialog';

type ViewState = 'list' | 'upload';

export const ArtisanSyncPage: React.FC = () => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('artisan_profiles', 'write');
  const canDelete = can('artisan_profiles', 'delete');

  const [viewState, setViewState] = useState<ViewState>('list');
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<{ ids: string[]; fromDetail: boolean } | null>(null);
  const [deleteNotice, setDeleteNotice] = useState<string | null>(null);

  const {
    profiles,
    totalProfiles,
    isLoadingList,
    filters,
    setFilters,
    page,
    size,
    setPage,
    setSize,
    selectedProfileId,
    setSelectedProfileId,
    selectedProfile,
    isLoadingDetail,
    workOrders,
    isLoadingWorkOrders,
    syncMutation,
    deleteMutation,
    bulkDeleteMutation,
  } = useArtisanSyncPage({ isSyncModalOpen });

  const openProfile = useCallback((id: string) => {
    setSelectedProfileId(id);
    setIsDetailModalOpen(true);
    setViewState('list');
  }, [setSelectedProfileId]);

  const closeDetail = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedProfileId(null);
    setIsSyncModalOpen(false);
    setSyncError(null);
  }, [setSelectedProfileId]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const selectable = (profiles ?? []).filter(p => !p.is_synced).map(p => p.id);
    setSelectedIds(prev =>
      selectable.every(id => prev.has(id)) ? new Set() : new Set(selectable)
    );
  }, [profiles]);

  const confirmDelete = useCallback(() => {
    if (!pendingDelete) return;
    const { ids, fromDetail } = pendingDelete;
    setDeleteNotice(null);

    if (ids.length === 1) {
      deleteMutation.mutate(ids[0], {
        onSuccess: () => {
          setPendingDelete(null);
          setSelectedIds(new Set());
          if (fromDetail) closeDetail();
        },
        onError: (error: unknown) => {
          setPendingDelete(null);
          // A 409 names the work order still holding the profile, so the notice can point
          // the user at where to unlink instead of just refusing.
          const blocking = getBlockingWorkOrder(error);
          setDeleteNotice(
            blocking && blocking !== '-'
              ? t('artisanSync.delete.blocked', { number: blocking })
              : getErrorMessage(error, t('artisanSync.delete.failed'))
          );
        },
      });
      return;
    }

    bulkDeleteMutation.mutate(ids, {
      onSuccess: (response) => {
        setPendingDelete(null);
        setSelectedIds(new Set());
        // A blocked id is a normal outcome, not an error: the user asked to delete what
        // could be deleted, and the rest stay with a reason.
        if (response.blocked > 0) {
          setDeleteNotice(t('artisanSync.delete.someBlocked', { count: response.blocked }));
        }
      },
      onError: (error: unknown) => {
        setPendingDelete(null);
        setDeleteNotice(getErrorMessage(error, t('artisanSync.delete.failed')));
      },
    });
  }, [pendingDelete, deleteMutation, bulkDeleteMutation, closeDetail, t]);

  const isDeleting = deleteMutation.isPending || bulkDeleteMutation.isPending;

  const renderList = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('artisanSync.page.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('artisanSync.page.subtitle')}</p>
        </div>
        {canWrite && (
          <button
            onClick={() => setViewState('upload')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            {t('artisanSync.page.uploadNewFile')}
          </button>
        )}
      </div>

      {deleteNotice && (
        <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-md text-sm flex items-start justify-between gap-3">
          <span>{deleteNotice}</span>
          <button onClick={() => setDeleteNotice(null)} className="text-amber-900 font-medium">
            {t('auth.common.close')}
          </button>
        </div>
      )}

      <ArtisanProfileTable
        profiles={profiles}
        isLoading={isLoadingList}
        filters={filters}
        onFiltersChange={setFilters}
        page={page}
        size={size}
        total={totalProfiles}
        onPageChange={setPage}
        onPageSizeChange={setSize}
        onSelectProfile={openProfile}
        canDelete={canDelete}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onDeleteSelected={() => setPendingDelete({ ids: Array.from(selectedIds), fromDetail: false })}
        onDeleteProfile={(id) => setPendingDelete({ ids: [id], fromDetail: false })}
        isDeleting={isDeleting}
      />
    </div>
  );

  return (
    <>
      {viewState === 'list' && renderList()}
      {viewState === 'upload' && (
        <ArtisanUploadPanel
          canWrite={canWrite}
          onBack={() => setViewState('list')}
          onOpenProfile={openProfile}
        />
      )}

      <ArtisanProfileDetailModal
        isOpen={isDetailModalOpen}
        onClose={closeDetail}
        profile={selectedProfile}
        isLoading={isLoadingDetail}
        canWrite={canWrite}
        canDelete={canDelete}
        workOrders={workOrders}
        isLoadingWorkOrders={isLoadingWorkOrders}
        isSyncModalOpen={isSyncModalOpen}
        onOpenSyncModal={() => {
          setIsSyncModalOpen(true);
          setSyncError(null);
        }}
        onCloseSyncModal={() => {
          setIsSyncModalOpen(false);
          setSyncError(null);
        }}
        onSync={(workOrderId) => {
          if (!selectedProfileId) return;
          syncMutation.mutate(
            { profileId: selectedProfileId, workOrderId },
            {
              onSuccess: () => {
                setIsSyncModalOpen(false);
                setSyncError(null);
              },
              onError: (error: unknown) => {
                setSyncError(getErrorMessage(error, t('artisanSync.errors.syncProfile')));
              },
            }
          );
        }}
        isSyncing={syncMutation.isPending}
        syncError={syncError}
        onDelete={() => {
          if (!selectedProfileId) return;
          setPendingDelete({ ids: [selectedProfileId], fromDetail: true });
        }}
      />

      <ArtisanDeleteDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        count={pendingDelete?.ids.length ?? 0}
        isDeleting={isDeleting}
      />
    </>
  );
};
